import { useRef, useCallback } from "react";
import { Trans } from "@lingui/macro";
import { Badge } from "@app/components/ui/badge";

export interface GroundingSpan {
  doc_id: string;
  doc_name: string;
  doc_date?: string | null;
  doc_type?: string | null;
  value: unknown;
  context_before: string;
  matched_text: string;
  context_after: string;
  char_start: number;
  char_end: number;
}

export interface MergeConflictCandidate {
  value: unknown;
  evidence?: string | null;
  chunk_idx?: number | null;
  chunk_max_date?: string | null;
}

export interface MergeConflict {
  strategy: string;
  candidates: MergeConflictCandidate[];
  winning_value: unknown;
}

export interface GroundingPair {
  /** Verbatim fragment the LLM cited (left side of the `→`). */
  quote: string;
  /** Brief CoT reasoning the LLM emitted (right side of the `→`). */
  thought: string;
}

export interface GroundingEntry {
  /** One pair per cited item — single entry for scalars/1-item arrays, one
   * per item for multi-item arrays (LLM emits them separated by ` ;; `). */
  pairs: GroundingPair[];
  /** First pair's verbatim quote — kept for backward compatibility with code
   * paths that only consume a single quote. */
  quote: string;
  /** First pair's thought — kept for backward compatibility. */
  thought: string;
  /** Context-rich spans aggregated from all pair quotes. */
  spans: GroundingSpan[];
  /** ``grounded`` = at least one quote found; ``metadata`` = form-sourced;
   * ``unverified`` = value non-null but no source span found. */
  status: "grounded" | "metadata" | "unverified";
  merge_conflict?: MergeConflict;
}

export interface GroundingField {
  field_key: string;
  name: string;
  /** Optional ontology group name — when present, the FE heatmap groups
   * fields by this attribute to surface per-group grounding coverage. */
  group_name?: string | null;
}

interface DocumentGroundingViewerProps {
  grounding: Record<string, GroundingEntry>;
  fields: GroundingField[];
  extractedValues: Record<string, unknown>;
}

// Color palette — blue/green/purple spectrum, no amber/orange (per project rules)
const FIELD_COLORS = [
  "#1752B7",
  "#059669",
  "#7C3AED",
  "#0891B2",
  "#2563EB",
  "#16A34A",
  "#9333EA",
  "#0284C7",
  "#0D9488",
  "#1D4ED8",
];

function getFieldColor(index: number): string {
  return FIELD_COLORS[index % FIELD_COLORS.length];
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "oui" : "non";
  return String(value);
}

interface ThoughtLineProps {
  pair: GroundingPair;
  color: string;
}

/** Renders one ``<quote> → <thought>`` pair. Quote is shown in muted small caps,
 * thought in italic right after the arrow. */
function ThoughtLine({ pair, color }: ThoughtLineProps) {
  if (!pair.quote && !pair.thought) return null;
  return (
    <div className="text-xs flex items-baseline flex-wrap gap-1 min-w-0">
      {pair.quote && (
        <span
          className="font-mono text-slate-500 truncate min-w-0"
          title={pair.quote}
        >
          « {pair.quote} »
        </span>
      )}
      {pair.thought && (
        <>
          <span className="font-mono flex-shrink-0" style={{ color }}>
            →
          </span>
          <span className="italic text-slate-600 break-words [overflow-wrap:anywhere]">
            {pair.thought}
          </span>
        </>
      )}
    </div>
  );
}

interface ThoughtListProps {
  pairs: GroundingPair[];
  color: string;
}

/** Renders the LLM's full CoT trace: one ThoughtLine per cited item. */
function ThoughtList({ pairs, color }: ThoughtListProps) {
  const visible = pairs.filter((p) => p.quote || p.thought);
  if (visible.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 min-w-0">
      {visible.map((pair, i) => (
        <ThoughtLine key={i} pair={pair} color={color} />
      ))}
    </div>
  );
}

interface SpanCardProps {
  span: GroundingSpan;
  color: string;
}

function SpanCard({ span, color }: SpanCardProps) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden min-w-0">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-xs min-w-0">
        <span className="font-semibold text-slate-700 truncate min-w-0">{span.doc_name || span.doc_id}</span>
        {span.doc_date && <span className="text-slate-400 flex-shrink-0">{span.doc_date}</span>}
        {span.doc_type && (
          <Badge variant="outline" className="text-3xs text-slate-500 border-slate-300 flex-shrink-0">
            {span.doc_type}
          </Badge>
        )}
        <span className="ml-auto text-slate-400 flex-shrink-0">
          [{span.char_start}–{span.char_end}]
        </span>
      </div>
      <div className="p-3 text-xs leading-relaxed text-slate-700 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
        <span className="text-slate-400">{span.context_before}</span>
        <mark className="px-0.5 rounded" style={{ backgroundColor: `${color}22`, color }}>
          {span.matched_text}
        </mark>
        <span className="text-slate-400">{span.context_after}</span>
      </div>
    </div>
  );
}

interface MergeConflictCardProps {
  conflict: MergeConflict;
}

function MergeConflictCard({ conflict }: MergeConflictCardProps) {
  return (
    <div className="border border-primary/30 rounded-lg overflow-hidden bg-primary/5">
      <div className="px-3 py-2 border-b border-primary/20 flex items-center gap-2 text-xs">
        <span className="font-semibold text-primary">
          <Trans>Conflit de fusion résolu</Trans>
        </span>
        <Badge variant="outline" className="text-3xs text-primary border-primary/30">
          {conflict.strategy}
        </Badge>
        <span className="ml-auto text-slate-500">
          <Trans>Valeur retenue :</Trans> <strong>{formatValue(conflict.winning_value)}</strong>
        </span>
      </div>
      <div className="p-3 text-xs">
        <p className="font-medium text-slate-600 mb-1">
          <Trans>Candidats :</Trans>
        </p>
        <ul className="space-y-1">
          {conflict.candidates.map((c, idx) => (
            <li key={idx} className="flex items-start gap-2 flex-wrap">
              <span className="text-slate-400 flex-shrink-0">#{idx}</span>
              <span className="text-slate-700">{formatValue(c.value)}</span>
              {c.evidence && (
                <span className="text-slate-400 italic truncate">« {c.evidence} »</span>
              )}
              {c.chunk_max_date && <span className="text-slate-400 ml-auto">{c.chunk_max_date}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function DocumentGroundingViewer({
  grounding,
  fields,
  extractedValues,
}: DocumentGroundingViewerProps) {
  const fieldRefs = useRef<Map<string, HTMLElement>>(new Map());
  const rightPanelRef = useRef<HTMLDivElement | null>(null);

  const fieldColorMap: Record<string, string> = {};
  fields.forEach((f, idx) => {
    fieldColorMap[f.field_key] = getFieldColor(idx);
  });

  const scrollToField = useCallback((fieldKey: string) => {
    const el = fieldRefs.current.get(fieldKey);
    const container = rightPanelRef.current;
    if (!el || !container) return;
    // Manual scroll on the container instead of ``scrollIntoView``: the modal
    // creates a fixed-position dialog and the default behaviour can scroll
    // the page behind the modal (or do nothing visible) rather than the
    // right panel itself. Computing relative offset keeps the scroll inside
    // the intended container.
    const top = el.offsetTop - container.offsetTop;
    container.scrollTo({ top, behavior: "smooth" });
  }, []);

  if (!grounding || Object.keys(grounding).length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-500 text-sm">
        <Trans>Données de sourçage non disponibles pour cette extraction.</Trans>
      </div>
    );
  }

  // Build the ordered list of fields to render — only those that have an entry
  // in grounding (whether grounded, unverified, or merge_conflict-only). Keeps
  // the iteration order of ``fields`` so the FE matches the ontology order.
  const renderedFields = fields.filter((f) => f.field_key in grounding);

  return (
    // ``min-w-0 w-full``: the modal uses a CSS grid layout (DialogContent's
    // base style). Grid items default to ``min-width: auto`` which is the
    // content's intrinsic width, so long source quotes inside SpanCard could
    // push this flex row wider than the modal even with ``max-w-5xl`` on the
    // dialog itself. Clamping the grid item to ``min-width: 0`` is the
    // standard fix for "text overflows a flex/grid descendant" in TailwindCSS.
    <div className="flex gap-4 h-[65vh] min-h-0 min-w-0 w-full overflow-hidden">
      {/* Left panel — extracted fields list */}
      <div className="w-64 flex-shrink-0 flex flex-col gap-2 overflow-y-auto pr-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
          <Trans>Champs extraits</Trans>
        </p>

        {renderedFields.length === 0 && (
          <p className="text-xs text-slate-400">
            <Trans>Aucun champ extrait.</Trans>
          </p>
        )}

        {renderedFields.map((field) => {
          const entry = grounding[field.field_key];
          const value = extractedValues[field.field_key];
          const color = fieldColorMap[field.field_key];
          const spanCount = entry.spans.length;
          const isUnverified = entry.status === "unverified";
          const isMetadata = entry.status === "metadata";
          return (
            <button
              key={field.field_key}
              onClick={() => scrollToField(field.field_key)}
              className="text-left w-full rounded-md p-2 hover:bg-slate-100 transition-colors border border-slate-200"
              title="Aller à la source"
            >
              <div className="flex items-start gap-2">
                <span
                  className="mt-1 flex-shrink-0 w-3 h-3 rounded-full"
                  style={{ backgroundColor: isUnverified ? "#94A3B8" : color }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-700 truncate">{field.name}</p>
                  <p
                    className="text-xs truncate"
                    style={{ color: isUnverified ? "#64748B" : color }}
                  >
                    {formatValue(value)}
                  </p>
                </div>
                {isUnverified ? (
                  <Badge
                    variant="outline"
                    className="ml-auto flex-shrink-0 text-3xs px-1 border-slate-400 text-slate-500"
                  >
                    <Trans>non vérifié</Trans>
                  </Badge>
                ) : isMetadata ? (
                  <Badge
                    variant="outline"
                    className="ml-auto flex-shrink-0 text-3xs px-1 border-primary/40 text-primary"
                  >
                    <Trans>formulaire</Trans>
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="ml-auto flex-shrink-0 text-3xs px-1"
                    style={{ borderColor: color, color }}
                  >
                    {spanCount}
                  </Badge>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Vertical separator */}
      <div className="w-px bg-slate-200 flex-shrink-0" />

      {/* Right panel — spans grouped by field, with merge conflicts inline */}
      <div ref={rightPanelRef} className="flex-1 min-w-0 flex flex-col gap-4 overflow-y-auto overflow-x-hidden">
        {renderedFields.map((field) => {
          const entry = grounding[field.field_key];
          const color = fieldColorMap[field.field_key];
          const value = extractedValues[field.field_key];
          return (
            <section
              key={field.field_key}
              ref={(el) => {
                if (el) {
                  fieldRefs.current.set(field.field_key, el);
                } else {
                  fieldRefs.current.delete(field.field_key);
                }
              }}
              className="flex flex-col gap-2 min-w-0"
            >
              <header className="flex items-baseline gap-2 min-w-0">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: entry.status === "unverified" ? "#94A3B8" : color }}
                />
                <h3 className="text-sm font-semibold text-slate-800 truncate min-w-0">{field.name}</h3>
                <span className="text-xs text-slate-500 truncate min-w-0 flex-shrink">
                  {formatValue(value)}
                </span>
                {entry.status === "unverified" && (
                  <Badge
                    variant="outline"
                    className="ml-auto text-3xs px-1 border-slate-400 text-slate-500"
                  >
                    <Trans>non vérifié — aucune source trouvée</Trans>
                  </Badge>
                )}
                {entry.status === "metadata" && (
                  <Badge
                    variant="outline"
                    className="ml-auto text-3xs px-1 border-primary/40 text-primary"
                  >
                    <Trans>variable de formulaire — pas de grounding textuel</Trans>
                  </Badge>
                )}
              </header>
              {/* LLM chain-of-thought trace — one line per cited item (single
                  line for scalars, multiple for multi-item arrays). */}
              <ThoughtList pairs={entry.pairs} color={color} />
              {entry.merge_conflict && <MergeConflictCard conflict={entry.merge_conflict} />}
              {entry.spans.map((span, idx) => (
                <SpanCard key={`${span.doc_id}-${span.char_start}-${idx}`} span={span} color={color} />
              ))}
              {entry.status === "unverified" && entry.spans.length === 0 && !entry.merge_conflict && (
                <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-lg p-3 break-words [overflow-wrap:anywhere]">
                  <Trans>
                    Aucun extrait de document n'a pu être associé à cette valeur. Le LLM n'a pas fourni de
                    citation littérale ou la citation n'apparaît pas verbatim dans les documents — potentielle
                    hallucination à auditer.
                  </Trans>
                </div>
              )}
              {entry.status === "metadata" && entry.spans.length === 0 && !entry.merge_conflict && (
                <div className="text-xs text-slate-500 italic border border-primary/20 bg-primary/5 rounded-lg p-3 break-words [overflow-wrap:anywhere]">
                  <Trans>
                    Valeur issue d'une variable structurée d'un formulaire (ou d'une formule dérivée). Le
                    grounding textuel ne s'applique pas — la source est le champ du formulaire, pas le
                    texte libre du dossier.
                  </Trans>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
